import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { makeStyles } from '@material-ui/styles';
import { Box, Button, Card, CardContent, Typography } from '@material-ui/core';
import EmailIcon from '@material-ui/icons/Email';
import { APP_PATH_LOGIN, MAIN_APP_NAME } from '../../config';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(3)
  },
  card: {
    maxWidth: 700,
    width: '100%',
    boxShadow: theme.shadows[10]
  },
  header: {
    textAlign: 'center',
    marginBottom: theme.spacing(2)
  },
  iconContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(3)
  },
  successIcon: {
    fontSize: 100,
    color: theme.palette.success.main,
    animation: '$scaleIn 0.5s ease-out'
  },
  '@keyframes scaleIn': {
    '0%': {
      transform: 'scale(0)',
      opacity: 0
    },
    '50%': {
      transform: 'scale(1.1)'
    },
    '100%': {
      transform: 'scale(1)',
      opacity: 1
    }
  },
  content: {
    textAlign: 'center'
  },
  title: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text'
  },
  emoji: {
    fontSize: '3rem',
    marginBottom: theme.spacing(2)
  },
  message: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2.5),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    borderLeft: `4px solid ${theme.palette.primary.main}`
  },
  emailIcon: {
    fontSize: 36,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1)
  },
  steps: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    textAlign: 'left',
    padding: theme.spacing(2)
  },
  step: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(2),
    '&:last-child': {
      marginBottom: 0
    }
  },
  stepNumber: {
    minWidth: 32,
    height: 32,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    marginRight: theme.spacing(2),
    fontSize: '0.875rem'
  },
  stepContent: {
    flex: 1,
    paddingTop: 4
  },
  actions: {
    marginTop: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2)
  },
  emphasizedText: {
    fontWeight: 600,
    color: theme.palette.primary.main
  },
  divider: {
    margin: theme.spacing(3, 0),
    height: 1,
    backgroundColor: theme.palette.divider
  }
}));

const RegistrationSuccess = () => {
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <Card className={classes.card}>
        <CardContent>
          <div className={classes.header}>
            <Typography variant="h2" color="textPrimary">
              {MAIN_APP_NAME}
            </Typography>
          </div>

          <div className={classes.content}>
            <div className={classes.emoji}>
              <span role="img" aria-label="Party celebration">
                🎉
              </span>
            </div>

            <Typography variant="h2" className={classes.title} gutterBottom>
              Welcome Aboard!
            </Typography>

            <Typography color="textSecondary" variant="h5" gutterBottom>
              Your account has been created successfully. One more step to get
              started!
            </Typography>

            <Box className={classes.message}>
              <EmailIcon className={classes.emailIcon} />
              <Typography
                variant="h6"
                gutterBottom
                style={{ marginBottom: '8px' }}>
                <span className={classes.emphasizedText}>
                  Check Your Email!
                </span>
              </Typography>
              <Typography color="textSecondary" variant="body2">
                We've sent an activation link to verify your email address.
                Please check your inbox (and spam folder) to complete your
                registration.
              </Typography>
            </Box>

            <Box className={classes.steps}>
              <Typography
                variant="h6"
                gutterBottom
                style={{ textAlign: 'center' }}>
                Next Steps:
              </Typography>

              <div className={classes.step}>
                <div className={classes.stepNumber}>1</div>
                <div className={classes.stepContent}>
                  <Typography variant="body1">
                    <strong>Open your email</strong>
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Look for an email from {MAIN_APP_NAME} with the subject
                    "Activate Your Account"
                  </Typography>
                </div>
              </div>

              <div className={classes.step}>
                <div className={classes.stepNumber}>2</div>
                <div className={classes.stepContent}>
                  <Typography variant="body1">
                    <strong>Click the activation link</strong>
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    This will verify your email address and activate your
                    account
                  </Typography>
                </div>
              </div>

              <div className={classes.step}>
                <div className={classes.stepNumber}>3</div>
                <div className={classes.stepContent}>
                  <Typography variant="body1">
                    <strong>Start hiring amazing remote talent!</strong>
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Once activated, you can sign in and post your first job
                    listing
                  </Typography>
                </div>
              </div>
            </Box>

            <div className={classes.divider}></div>

            <Typography color="textSecondary" variant="body2" gutterBottom>
              <strong>Important:</strong> The activation link is valid for 24
              hours. If you don't see the email, please check your spam folder.
            </Typography>

            <div className={classes.actions}>
              <Button
                color="primary"
                fullWidth
                size="large"
                variant="contained"
                component={RouterLink}
                to={APP_PATH_LOGIN}>
                Go to Sign In Page
              </Button>

              <Typography color="textSecondary" variant="body2" align="center">
                Already activated your account? Sign in above to get started!
              </Typography>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RegistrationSuccess;
